import React from 'react';
import { MDXProvider } from '@mdx-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import '../images/foundation-icons/foundation-icons.css';
import '../scss/index.scss';
import Banner from './Banner';
import FamilySummary from './FamilySummary';
import Header from './Header';
import Footer from './Footer';
import Image from './Image';
import ImageRow from './ImageRow';
import Recipe from './Recipe';
import { FeaturedContent, FeaturedContentWithImage } from './FeaturedContent';
import FeaturedProducts from './FeaturedProductRow';
import Authentication from './Authentication';
import CartContext from './CartContext';
import { AcornGraphqlError } from '../fetcher';
import log from 'loglevel';

const NON_RETRYABLE_ERROR_CODES = [
  'BAD_USER_INPUT',
  'UPS_SHIPPING_ERROR', // Bad Address
];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,
    },
    mutations: {
      retry: (retryCount, error) => {
        if (
          error instanceof AcornGraphqlError &&
          NON_RETRYABLE_ERROR_CODES.includes(error?.errors[0]?.extensions?.code)
        ) {
          log.warn(
            { error },
            'queryClient: got BAD_USER_INPUT error, not retrying request',
          );
          return false;
        }
        return retryCount < 3;
      },
    },
  },
});

const Layout = ({ children }) => {
  const shortcodes = {
    FamilySummary,
    Image,
    ImageRow,
    Recipe,
    FeaturedContent,
    FeaturedContentWithImage,
    FeaturedProducts,
  };
  return (
    <QueryClientProvider client={queryClient}>
      <Authentication.Provider>
        <CartContext.Provider>
          <a className="skip-link" href="#main-content">
            Skip to main content
          </a>
          <header className="site-header">
            <Banner siteTitle="Mountain Valley Growers" />
            <Header />
          </header>
          <main id="main-content" tabIndex={-1} className="grid-container">
            <MDXProvider components={shortcodes}>{children}</MDXProvider>
          </main>
          <Footer />
          <ReactQueryDevtools initialIsOpen={false} />
        </CartContext.Provider>
      </Authentication.Provider>
    </QueryClientProvider>
  );
};

export default Layout;
